import { useState, useEffect } from "react";
import { useBranch } from "@/context/branch";
import { useToast } from "@/hooks/use-toast";
import { useListExpenses, useCreateExpense, useUpdateExpense, useDeleteExpense, getListExpensesQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2, Plus, Edit, Trash2, Download, CreditCard, Check, ChevronsUpDown } from "lucide-react";
import * as XLSX from "xlsx";
import { format } from "date-fns";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const EXPENSE_CATEGORIES = ["Rent", "Utilities", "Salaries", "Marketing", "Transport", "Maintenance", "Equipment", "Supplies", "Other"];

const schema = z.object({
  title: z.string().min(1, "Title is required"),
  amount: z.coerce.number().min(0.01),
  category: z.string().min(1, "Category is required"),
  description: z.string().optional(),
  date: z.string().min(1, "Date is required"),
});

type FormValues = z.infer<typeof schema>;

function ExpenseForm({ form, onSubmit, isPending }: {
  form: UseFormReturn<FormValues>;
  onSubmit: (data: FormValues) => void;
  isPending: boolean;
}) {
  const [categoryOpen, setCategoryOpen] = useState(false);
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField control={form.control} name="title" render={({ field }) => (
            <FormItem><FormLabel>Title</FormLabel><FormControl><Input autoFocus {...field} /></FormControl><FormMessage /></FormItem>
          )} />
          <FormField control={form.control} name="amount" render={({ field }) => (
            <FormItem><FormLabel>Amount (₦)</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
          )} />
          <FormField control={form.control} name="category" render={({ field }) => (
            <FormItem><FormLabel>Category</FormLabel>
              <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button type="button" variant="outline" role="combobox" aria-expanded={categoryOpen} data-testid="button-select-expense-category" className="w-full justify-between font-normal">
                      {field.value || "Select category"}<ChevronsUpDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandInput placeholder="Search categories…" data-testid="input-search-expense-category" />
                    <CommandList>
                      <CommandEmpty>No matching category.</CommandEmpty>
                      <CommandGroup>
                        {EXPENSE_CATEGORIES.map(category => (
                          <CommandItem key={category} value={category} onSelect={() => { field.onChange(category); setCategoryOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${field.value === category ? "opacity-100" : "opacity-0"}`} />{category}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="date" render={({ field }) => (
            <FormItem><FormLabel>Date</FormLabel><FormControl><Input type="date" {...field} /></FormControl><FormMessage /></FormItem>
          )} />
        </div>
        <FormField control={form.control} name="description" render={({ field }) => (
          <FormItem><FormLabel>Description</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
        )} />
        <Button type="submit" className="w-full" disabled={isPending}>Save Expense</Button>
      </form>
    </Form>
  );
}

export default function Expenses() {
  const queryClient = useQueryClient();
  const { activeBranchId, activeBranch } = useBranch();
  const { toast } = useToast();
  const [filterCat, setFilterCat] = useState("");
  const [filterCategoryOpen, setFilterCategoryOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    if (window.location.search.includes("add=1")) setIsCreateOpen(true);
  }, []);

  const { data: expenses, isLoading } = useListExpenses({
    category: filterCat || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    branchId: activeBranchId ?? undefined,
  });
  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const deleteExpense = useDeleteExpense();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: "", amount: 0, category: "", description: "", date: new Date().toISOString().split("T")[0] },
  });

  const onSubmit = (data: FormValues) => {
    if (editingId) {
      updateExpense.mutate({ id: editingId, data }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListExpensesQueryKey() });
          setEditingId(null); form.reset();
        },
        onError: () => toast({ title: "Failed to update expense", variant: "destructive" }),
      });
    } else {
      createExpense.mutate({ data: { ...data, branchId: activeBranchId ?? undefined } as any }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListExpensesQueryKey() });
          setIsCreateOpen(false); form.reset();
        },
        onError: () => toast({ title: "Failed to save expense", variant: "destructive" }),
      });
    }
  };

  const handleEdit = (e: any) => {
    setEditingId(e.id);
    form.reset({ title: e.title, amount: e.amount, category: e.category, description: e.description || "", date: e.date });
  };

  const handleDelete = (id: number) => {
    if (confirm("Delete this expense?")) {
      deleteExpense.mutate({ id }, {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getListExpensesQueryKey() }),
        onError: () => toast({ title: "Failed to delete expense", variant: "destructive" }),
      });
    }
  };

  const totalExpenses = expenses?.reduce((s, e) => s + Number(e.amount), 0) ?? 0;

  const exportCSV = () => {
    if (!expenses?.length) return;
    const rows = [...expenses].reverse().map(e => ({
      "Title": e.title,
      "Category": e.category,
      "Description": e.description || "",
      "Date": format(new Date(e.date), "MMM d, yyyy"),
      "Amount (₦)": Number(e.amount).toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expenses");
    const date = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `expenses_${date}.csv`, { bookType: "csv" });
  };

  const isPending = createExpense.isPending || updateExpense.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Expenses — {activeBranch?.name ?? "All Branches"}</h1>
          <p className="text-muted-foreground mt-1">Track and categorize spending for the selected location.</p>
        </div>
        <div className="flex items-center gap-2">
          {expenses && expenses.length > 0 && (
            <Button variant="outline" onClick={exportCSV}>
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          )}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingId(null); form.reset({ title: "", amount: 0, category: "", description: "", date: new Date().toISOString().split("T")[0] }); }}>
                <Plus className="w-4 h-4 mr-2" /> Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader>
              <ExpenseForm form={form} onSubmit={onSubmit} isPending={isPending} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Dialog open={editingId !== null} onOpenChange={(o) => !o && setEditingId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Expense</DialogTitle></DialogHeader>
          <ExpenseForm form={form} onSubmit={onSubmit} isPending={isPending} />
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-4 flex-wrap">
        <Popover open={filterCategoryOpen} onOpenChange={setFilterCategoryOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" role="combobox" aria-expanded={filterCategoryOpen} data-testid="button-filter-expense-category" className="w-44 justify-between">
              {filterCat && filterCat !== "all" ? filterCat : "All categories"}<ChevronsUpDown className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
            <Command>
              <CommandInput placeholder="Search categories…" data-testid="input-search-expense-category-filter" />
              <CommandList>
                <CommandEmpty>No matching category.</CommandEmpty>
                <CommandGroup>
                  {[{ value: "all", label: "All categories" }, ...EXPENSE_CATEGORIES.map(category => ({ value: category, label: category }))].map(option => (
                    <CommandItem key={option.value} value={option.label} onSelect={() => { setFilterCat(option.value === "all" ? "" : option.value); setFilterCategoryOpen(false); }}>
                      <Check className={`mr-2 h-4 w-4 ${(!filterCat && option.value === "all") || filterCat === option.value ? "opacity-100" : "opacity-0"}`} />{option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" placeholder="From" />
        <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" placeholder="To" />
        {(filterCat || startDate || endDate) && <Button variant="ghost" onClick={() => { setFilterCat(""); setStartDate(""); setEndDate(""); }}>Clear</Button>}
        {expenses && <span className="ml-auto text-sm font-semibold text-muted-foreground">Total: <span className="text-foreground">₦{totalExpenses.toFixed(2)}</span></span>}
      </div>

      <div className="border rounded-md overflow-hidden">
        <div className="overflow-y-auto max-h-[600px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
            ) : expenses?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-16">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-muted-foreground/50" />
                    </div>
                    <div>
                      <p className="font-semibold">No expenses recorded</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Track business spending to see accurate profit & loss on your reports.</p>
                    </div>
                    <Button size="sm" className="gap-2 mt-1" onClick={() => setIsCreateOpen(true)}>
                      <Plus className="w-4 h-4" /> Record First Expense
                    </Button>
                    <p className="text-xs text-muted-foreground/60">💡 Categorise expenses to get a breakdown in your P&L report</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : [...(expenses || [])].reverse().map(e => (
              <TableRow key={e.id}>
                <TableCell className="font-medium">{e.title}</TableCell>
                <TableCell><Badge variant="secondary">{e.category}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{e.description || "—"}</TableCell>
                <TableCell>{format(new Date(e.date), "MMM d, yyyy")}</TableCell>
                <TableCell className="text-right font-medium">₦{Number(e.amount).toFixed(2)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(e)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => handleDelete(e.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>
    </div>
  );
}
